import { useCallback, useMemo, useState } from "react";
import { CheckCircle2, FileImage, Loader2, Trash2, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { LoaderButton } from "@/components/ui/custom-buttons";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileDropzone, FileItem } from "@/components/ui/FileDropzone";
import InfoBulle from "@/components/ui/info-bulle";
import { useToast } from "@/hooks/use-toast";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useTranslation } from "react-i18next";
import { defaultPosition, optionsPosition } from "@/lib/image";
import { ImportTable } from "@/lib/import/import-types";
import {
  DEFAULT_IMAGE_LINK_RULES,
  ImageLinkItem,
  ImageLinkReport,
  ImageLinkRules,
  OccupiedPolicy,
  declareFiles,
  useAnalyseImageLink,
} from "@/api/image-link";
import { BATCH_IMPORT_IMAGE_ACCEPT } from "../batchImportConfig";
import { UseImageLinkImportReturn } from "./useImageLinkImport";
import { ImageLinkMatchReport } from "./ImageLinkMatchReport";
import { ImageLinkApproxMatches } from "./ImageLinkApproxMatches";

/**
 * Rattachement d'images en lot par n° d'enregistrement.
 *
 * Reste un **dialogue** et non une page : c'est reellement « deposer des
 * fichiers, regarder une barre » plus un ecran d'arbitrage. L'import Excel, lui,
 * est une page parce que son arbitrage compte des centaines de lignes.
 *
 * Ordre volontaire : on analyse les **noms** d'abord, on approuve, **puis** on
 * televerse. Personne ne doit envoyer 2 Go pour decouvrir que 100 fichiers ne
 * correspondent a rien.
 */
type Screen = "selection" | "report" | "running" | "recap";

export function ImageLinkImportDialog({
  table,
  open,
  onOpenChange,
  link,
}: {
  table: ImportTable;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  link: UseImageLinkImportReturn;
}) {
  const vt = useVisibleT();
  const { t, i18n } = useTranslation();
  const { toast } = useToast();

  const [files, setFiles] = useState<File[]>([]);
  const [rules, setRules] = useState<ImageLinkRules>(DEFAULT_IMAGE_LINK_RULES);
  const [occupied, setOccupied] = useState<OccupiedPolicy>("addAsAdditional");
  const [report, setReport] = useState<ImageLinkReport | null>(null);
  const [items, setItems] = useState<ImageLinkItem[]>([]);
  const [decisions, setDecisions] = useState(
    new Map<string, { accepted: boolean; recordId?: string }>(),
  );

  const { mutateAsync: analyse, isPending: analysing } = useAnalyseImageLink();

  /**
   * Le rapport et l'arbitrage tiennent sur **un seul** ecran : les compteurs en
   * haut, le tableau de validation juste en dessous quand il y a lieu. Separer
   * les deux obligerait a faire des aller-retours pour voir l'effet d'une regle
   * sur les compteurs.
   */
  const screen: Screen = link.done
    ? "recap"
    : link.running
      ? "running"
      : report == null
        ? "selection"
        : "report";

  const positions = optionsPosition[table as keyof typeof optionsPosition] ?? [];
  // Position visée par défaut, modifiable par l'utilisateur : elle s'applique
  // aux fichiers sans suffixe _recto/_verso.
  const [mainPosition, setMainPosition] = useState<string>(
    defaultPosition[table as keyof typeof defaultPosition],
  );
  // Réglages d'écriture appliqués à tout le lot. Les deux sont désactivés par
  // défaut : l'utilisateur active explicitement filigrane et/ou version HD.
  const [applyWatermark, setApplyWatermark] = useState(false);
  const [keepHd, setKeepHd] = useState(false);

  const approxItems = useMemo(
    () => items.filter((item) => item.state === "ask" || item.state === "ambiguous"),
    [items],
  );

  const runAnalyse = useCallback(
    async (nextRules: ImageLinkRules, nextOccupied: OccupiedPolicy) => {
      if (files.length === 0) return;
      try {
        const result = await analyse({
          table,
          files: declareFiles(files),
          rules: nextRules,
          occupied: nextOccupied,
          defaultPosition: mainPosition,
          locale: i18n.language,
        });
        setReport(result.report);
        setItems(result.items);
      } catch {
        toast({
          variant: "destructive",
          title: vt(
            "bo.imageLink.analyseError",
            "L'analyse des noms de fichiers a échoué.",
          ),
        });
      }
    },
    [analyse, files, table, toast, vt, mainPosition, i18n.language],
  );

  const reset = () => {
    setFiles([]);
    setReport(null);
    setItems([]);
    setDecisions(new Map());
    link.reset();
  };

  const close = (next: boolean) => {
    // On ne reinitialise pas pendant la course : l'utilisateur doit pouvoir
    // fermer et revenir.
    if (!next && !link.running && !link.done) reset();
    onOpenChange(next);
  };

  /** Fichiers retenus : les sûrs, plus ceux validés à la main. */
  const accepted = useMemo(() => {
    const result: ImageLinkItem[] = [];
    for (const item of items) {
      if (item.state === "auto") {
        result.push(item);
        continue;
      }
      const decision = decisions.get(item.fileName);
      if (!decision?.accepted) continue;
      result.push(
        decision.recordId
          ? {
              ...item,
              recordId: decision.recordId,
              recordLabel:
                item.candidates.find((c) => c.id === decision.recordId)?.label ??
                item.recordLabel,
            }
          : item,
      );
    }
    return result;
  }, [items, decisions]);

  const start = () => {
    const queue = link.prepare(files, accepted);
    if (queue.length === 0) {
      toast({
        variant: "destructive",
        title: vt("bo.imageLink.nothingToLink", "Aucune image à rattacher."),
      });
      return;
    }
    void link.run(queue, { applyWatermark, isHd: keepHd });
  };

  const downloadUnlinkable = () => {
    if (!report) return;
    const rows = [
      "fichier;raison",
      ...report.unlinkableFiles.map((entry) => `${entry.fileName};${entry.reason}`),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `images-non-liees-${table}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            {vt("bo.imageLink.title", "Lier des images à des fiches existantes")}
          </DialogTitle>
          <DialogDescription>
            {vt(
              "bo.imageLink.subtitle",
              "Le nom du fichier doit correspondre au n° d'enregistrement d'une fiche existante.",
            )}
          </DialogDescription>
        </DialogHeader>

        {screen === "selection" && (
          <div className="flex flex-col gap-4">
            <InfoBulle
              message={vt(
                "bo.imageLink.contract",
                "Exemple : 2019-042.jpg sera rattaché à la fiche n° 2019-042. Aucune nouvelle fiche ne sera créée.",
              )}
            />

            {files.length === 0 ? (
              <FileDropzone
                multiple
                accept={BATCH_IMPORT_IMAGE_ACCEPT}
                onDrop={(dropped) => setFiles(dropped)}
              >
                {() => (
                  <div className="flex flex-col items-center gap-2 py-8 text-center">
                    <FileImage className="w-8 h-8 text-gray-400" aria-hidden />
                    <span className="font-medium">
                      {vt(
                        "bo.imageLink.dropzone",
                        "Déposez vos images ici, ou cliquez pour les choisir",
                      )}
                    </span>
                  </div>
                )}
              </FileDropzone>
            ) : (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {files.length}{" "}
                    {vt("bo.imageLink.filesSelected", "images sélectionnées")}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => setFiles([])}>
                    <Trash2 className="w-4 h-4 mr-1" aria-hidden />
                    {vt("bo.imageLink.clear", "Vider")}
                  </Button>
                </div>
                <div className="max-h-40 overflow-y-auto flex flex-col gap-1">
                  {files.slice(0, 30).map((file) => (
                    <FileItem key={`${file.name}-${file.size}`} file={file} />
                  ))}
                  {files.length > 30 && (
                    <span className="text-xs text-gray-500 px-2">
                      … {files.length - 30}{" "}
                      {vt("bo.imageLink.andMore", "autres")}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">
                {vt("bo.imageLink.targetPosition", "Position visée par défaut")}
              </label>
              <Select value={mainPosition} onValueChange={setMainPosition}>
                <SelectTrigger className="max-w-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {positions.map((position) => (
                    <SelectItem key={position} value={position}>
                      {t(position)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-gray-500">
                {vt(
                  "bo.imageLink.positionHint",
                  "Les suffixes _recto et _verso placent automatiquement l'image à la bonne position.",
                )}
              </span>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">
                {vt(
                  "bo.imageLink.occupied.title",
                  "Si la fiche a déjà une image à cette position",
                )}
              </span>
              <RadioGroup
                value={occupied}
                onValueChange={(value) => setOccupied(value as OccupiedPolicy)}
                className="flex flex-col gap-1.5"
              >
                {(
                  [
                    [
                      "addAsAdditional",
                      vt(
                        "bo.imageLink.occupied.additional",
                        "Ajouter en photo supplémentaire (recommandé)",
                      ),
                    ],
                    [
                      "skip",
                      vt("bo.imageLink.occupied.skip", "Ne pas importer cette image"),
                    ],
                    [
                      "replace",
                      vt(
                        "bo.imageLink.occupied.replace",
                        "Remplacer l'image existante",
                      ),
                    ],
                  ] as Array<[OccupiedPolicy, string]>
                ).map(([value, label]) => (
                  <label
                    key={value}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <RadioGroupItem value={value} />
                    <span>{label}</span>
                  </label>
                ))}
              </RadioGroup>
              {occupied === "replace" && (
                <p className="text-sm text-red-800">
                  {vt(
                    "bo.imageLink.occupied.replaceWarning",
                    "L'image actuelle sera remplacée. Chaque remplacement devra être validé un par un.",
                  )}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium">
                {vt("bo.imageLink.options.title", "Options d'image")}
              </span>
              <label className="flex items-start justify-between gap-3 cursor-pointer">
                <span className="flex flex-col">
                  <span className="text-sm">
                    {vt("bo.imageLink.options.watermark", "Appliquer le filigrane")}
                  </span>
                  <span className="text-xs text-gray-500">
                    {vt(
                      "bo.imageLink.options.watermarkHint",
                      "Ajoute le filigrane de l'instance sur les images publiées.",
                    )}
                  </span>
                </span>
                <Switch
                  checked={applyWatermark}
                  onCheckedChange={setApplyWatermark}
                />
              </label>
              <label className="flex items-start justify-between gap-3 cursor-pointer">
                <span className="flex flex-col">
                  <span className="text-sm">
                    {vt("bo.imageLink.options.hd", "Conserver la version HD")}
                  </span>
                  <span className="text-xs text-gray-500">
                    {vt(
                      "bo.imageLink.options.hdHint",
                      "Garde le fichier haute définition d'origine (stockage plus lourd).",
                    )}
                  </span>
                </span>
                <Switch checked={keepHd} onCheckedChange={setKeepHd} />
              </label>
            </div>
          </div>
        )}

        {screen === "report" && report && (
          <div className="flex flex-col gap-5">
            <ImageLinkMatchReport
              report={report}
              rules={rules}
              analysing={analysing}
              onRulesChange={(next) => {
                setRules(next);
                setDecisions(new Map());
                void runAnalyse(next, occupied);
              }}
              onDownloadUnlinkable={downloadUnlinkable}
            />

            {approxItems.length > 0 && (
              <ImageLinkApproxMatches
                table={table}
                items={approxItems}
                files={files}
                decisions={decisions}
                onDecision={(fileName, acceptedValue, recordId) =>
                  setDecisions((current) => {
                    const next = new Map(current);
                    next.set(fileName, { accepted: acceptedValue, recordId });
                    return next;
                  })
                }
                onPositionChange={(fileName, position) =>
                  setItems((current) =>
                    current.map((item) =>
                      item.fileName === fileName ? { ...item, position } : item,
                    ),
                  )
                }
                onAcceptAll={() =>
                  setDecisions((current) => {
                    const next = new Map(current);
                    for (const item of approxItems) {
                      // Les ambigus sont exclus : ils exigent un choix explicite.
                      if (item.state === "ambiguous") continue;
                      next.set(item.fileName, { accepted: true });
                    }
                    return next;
                  })
                }
              />
            )}
          </div>
        )}

        {screen === "running" && (
          <div className="flex flex-col gap-3" aria-live="polite">
            <span className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              {vt("bo.imageLink.running", "Rattachement en cours…")}
            </span>
            <Progress value={link.percent} className="h-2" />
            <span className="text-sm text-gray-600 tabular-nums">
              {link.finished} / {link.total}
              {link.inFlight && ` · ${link.inFlight.fileName}`}
            </span>
            <p className="text-sm text-gray-500">
              {vt(
                "bo.imageLink.runningHint",
                "Vous pouvez fermer cette fenêtre : le rattachement continue.",
              )}
            </p>
          </div>
        )}

        {screen === "recap" && (
          <div className="flex flex-col gap-3">
            <div className="flex gap-4 text-sm">
              <span className="flex items-center gap-1.5 text-green-800">
                <CheckCircle2 className="w-4 h-4" aria-hidden />
                {link.successCount}{" "}
                {vt("bo.imageLink.recap.linked", "images liées")}
              </span>
              {link.errorCount > 0 && (
                <span className="flex items-center gap-1.5 text-red-800">
                  <XCircle className="w-4 h-4" aria-hidden />
                  {link.errorCount} {vt("bo.imageLink.recap.failed", "échecs")}
                </span>
              )}
            </div>

            <div className="max-h-64 overflow-y-auto flex flex-col gap-1">
              {link.items.map((item) => (
                <div
                  key={item.uid}
                  className="flex items-center gap-2 text-sm py-1 border-b last:border-0"
                >
                  {item.thumbnailUrl ? (
                    <img
                      src={item.thumbnailUrl}
                      alt=""
                      className="h-8 w-8 object-cover rounded"
                    />
                  ) : (
                    <FileImage className="h-8 w-8 text-gray-300" aria-hidden />
                  )}
                  <span className="font-mono text-xs truncate flex-1">
                    {item.fileName}
                  </span>
                  <span className="text-xs text-gray-500">{item.recordLabel}</span>
                  {item.status === "success" ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" aria-hidden />
                  ) : item.status === "error" ? (
                    <span className="text-xs text-red-700">{item.error}</span>
                  ) : (
                    <span className="text-xs text-gray-400">
                      {vt("bo.imageLink.recap.skipped", "ignorée")}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          {screen === "selection" && (
            <>
              <Button variant="ghost" onClick={() => close(false)}>
                {vt("bo.imageLink.cancel", "Annuler")}
              </Button>
              <LoaderButton
                isLoading={analysing}
                disabled={files.length === 0 || analysing}
                onClick={() => void runAnalyse(rules, occupied)}
              >
                {vt("bo.imageLink.analyse", "Analyser les correspondances")} (
                {files.length})
              </LoaderButton>
            </>
          )}

          {screen === "report" && (
            <>
              <Button variant="ghost" onClick={reset}>
                {vt("bo.imageLink.back", "Changer de sélection")}
              </Button>
              <Button disabled={accepted.length === 0} onClick={start}>
                {vt("bo.imageLink.start", "Rattacher")} ({accepted.length})
              </Button>
            </>
          )}

          {screen === "running" && (
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {vt("bo.imageLink.hide", "Masquer")}
            </Button>
          )}

          {screen === "recap" && (
            <>
              <Button variant="ghost" onClick={reset}>
                {vt("bo.imageLink.newBatch", "Nouveau lot")}
              </Button>
              <Button onClick={() => close(false)}>
                {vt("bo.imageLink.close", "Fermer")}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default ImageLinkImportDialog;
